import { Fragment, useState } from 'react';

import { Listbox, Transition } from '@headlessui/react';
import { ChevronUpDownIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';

import { THEME_CIRCLE_STYLES } from '@lessonPlanner/constants';

import { formClasses } from './utils/FormStyles';
import { Label } from './utils/Label';

interface PanelOption {
  id: string;
  name: string;
  backgroundImageTitle?: string;
  [key: string]: unknown;
}

interface PanelDropdownFieldProps {
  value: PanelOption;
  onChange: (value: PanelOption) => void;
  options: PanelOption[];
  label?: React.ReactNode;
  errorMessage?: string;
  id?: string;
  className?: string;
  disabled?: boolean;
  link?: string;
  tooltip?: string;
}

export function PanelDropdownField({
  value,
  onChange,
  options,
  label,
  errorMessage,
  id,
  className,
  disabled = false,
  link = '',
  tooltip = '',
}: PanelDropdownFieldProps) {
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <div className={className}>
      {label && (
        <Label id={id} link={link}>
          {label}
        </Label>
      )}
      <Listbox disabled={disabled} value={value} onChange={onChange}>
        <div
          className="relative"
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          <Listbox.Button
            className={clsx(formClasses, 'h-[38px] bg-white text-left')}
            onClick={() => setShowTooltip(false)}
          >
            <div className="flex items-center gap-2">
              <div
                className={clsx(
                  'flex h-5 w-5 items-center justify-center rounded-full text-sm',
                  THEME_CIRCLE_STYLES[value.id],
                )}
                style={{
                  backgroundImage: `url(${value.backgroundImageTitle})`,
                }}
              >
                <span>{value.name[0]}</span>
              </div>
              <span className="text-sm">{value.name}</span>
            </div>
            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
              <ChevronUpDownIcon
                aria-hidden="true"
                className="h-5 w-5 text-gray-400"
              />
            </span>
          </Listbox.Button>
          {tooltip && showTooltip && (
            <div className="absolute left-0 z-50 mt-2 rounded-md border border-gray-200 bg-white p-2 text-sm text-gray-600 shadow-md">
              {tooltip}
            </div>
          )}
          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Listbox.Options className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5">
              {options.map((option) => (
                <Listbox.Option
                  key={option.id}
                  value={option}
                  className={({ active }) =>
                    `relative cursor-pointer select-none py-2 pl-3 pr-9 ${
                      active ? 'bg-blue-50' : ''
                    }`
                  }
                  onClick={() => setShowTooltip(false)}
                >
                  <div className="flex items-center gap-2">
                    <div
                      className={clsx(
                        'flex h-6 w-6 items-center justify-center rounded-full text-sm',
                        THEME_CIRCLE_STYLES[option.id],
                      )}
                      style={{
                        backgroundImage: `url(${option.backgroundImageTitle})`,
                      }}
                    >
                      <span>{option.name[0]}</span>
                    </div>
                    <span className="text-sm">{option.name}</span>
                  </div>
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </Transition>
        </div>
      </Listbox>
      {errorMessage && <p className="text-red-500">{errorMessage}</p>}
    </div>
  );
}
